import { ILensParam } from '@/custom/menicon';
import { Laterality, LensOption, LensPropertyVisibility, MeniconLensPropertyDetails } from '@/custom/menicon/models';
import { between, required, step, validate } from '@/custom/menicon/helpers/validators.helper';
import {
  COMPRESSION_FACTOR_PARAMETER_NAME,
  MATERIAL_PARAMETER_NAME,
  TRIAL_LENS_PARAMETER_NAME
} from '@/custom/menicon/constants';

export const lensParamsToLensFitting = (lensParams: Array<ILensParam>): { [key: string]: string | MeniconLensPropertyDetails } =>
  lensParams.reduce((r, k) => {
    if (k.visibility === LensPropertyVisibility.VISIBLE) {
      r[k.name] = k.value;
    } else {
      r[k.name] = {
        visibility: k.visibility as LensPropertyVisibility,
        value: k.value
      };
    }
    return r;
  }, {} as { [key: string]: string | MeniconLensPropertyDetails });

export const lensFittingtoLensParams = (
  lensFitting: { [key: string]: string | MeniconLensPropertyDetails},
  materialName: string
): Array<ILensParam> =>
  Object.keys(lensFitting).reduce((acc, name) => {
    let options: ILensParam['options'] = [];
    if (name === TRIAL_LENS_PARAMETER_NAME) {
      options = [
        {
          label: 'Yes',
          value: 1,
          id: 0
        },
        {
          label: 'No',
          value: 0,
          id: 1
        }
      ];
    }
    if (name === COMPRESSION_FACTOR_PARAMETER_NAME) {
      options = [
        {
          label: 'Low',
          value: -0.5,
          id: 0
        },
        {
          label: 'Medium',
          value: -1,
          id: 1
        },
        {
          label: 'High',
          value: -1.5,
          id: 2
        }
      ];
    }
    if (name === MATERIAL_PARAMETER_NAME) {
      options = [
        {
          label: materialName,
          value: getLensParamValue(lensFitting[name]).toString(),
          id: 'true'
        }
      ];
    }
    const value: ILensParam = {
      name,
      value: getLensParamValue(lensFitting[name]).toString(),
      default: getLensParamValue(lensFitting[name]).toString(),
      step: null,
      min: null,
      max: null,
      readOnly: true,
      precision: null,
      visibility: getParamVisibility(lensFitting[name]),
      options
    };
    return [...acc, value];
  }, [] as Array<ILensParam>);

export const getLensParamValue = (value: string | number | MeniconLensPropertyDetails | null | undefined): string => {
  if (!value) {
    return '';
  }
  if (typeof value === 'object') {
    return (value.value)? value.value.toString() : '';
  }
  return value.toString();
};
export const getParamVisibility = (value: string | MeniconLensPropertyDetails) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return LensPropertyVisibility.VISIBLE;
  }
  return value.visibility;
};
export const removeEyePrefix = (data: { [key: string]: any }) =>
  Object.keys(data).reduce(
    (acc, key) => ({
      ...acc,
      [key.substr(2)]: data[key]
    }),
    {} as { [key: string]: any }
  );

export const addEyePrefix = (data: { [key: string]: any }, laterality: Laterality | 'both') => {
  const prefix = laterality === Laterality.right ? 'r_' : laterality === Laterality.left ? 'l_' : 'combined_';
  return Object.keys(data).reduce(
    (acc, key) => ({
      ...acc,
      [`${prefix}${key}`]: data[key]
    }),
    {} as { [key: string]: any }
  );
};

export const lensScores = ['Best', 'Good', 'Poor', 'NotUsable'];

export const getBestLens = (sortedList: Record<Laterality, LensOption[]>) => {
  const rightList = sortedList.right.filter((lens) => !lens.disabled);
  const leftList = sortedList.left.filter((lens) => !lens.disabled);
  const bestRight = rightList[0] || null;
  const bestLeft = leftList[0] || null;
  if (bestLeft && bestRight) {
    const rightScore = lensScores.findIndex((score) => score === bestRight.score);
    const leftScore = lensScores.findIndex((score) => score === bestLeft.score);
    return rightScore < leftScore
      ? {
        id: bestRight.value,
        laterality: Laterality.right
      }
      : {
        id: bestLeft.value,
        laterality: Laterality.left
      };
  }
  return bestRight
    ? {
      id: bestRight.value,
      laterality: Laterality.right
    }
    : {
      id: bestLeft?.value || '',
      laterality: Laterality.left
    };
};

export const validateLensParameter = (parameter: ILensParam, value: string) => validate({
  [parameter.name]: {
    required,
    ...(parameter.max ? { between: between(+(parameter.min || 0), +parameter.max) } : {}),
    ...(parameter.step ? { step: step(parameter.step) } : {})
  }
}, {
  [parameter.name]: value
});

export const getOppositeLaterality = (laterality: Laterality) =>
  laterality === Laterality.right ? Laterality.left : Laterality.right;
